import { Component, DestroyRef, HostListener, inject, OnInit } from '@angular/core';
import { HtmlLangService, PageTitleStrategy, UserService } from './shared/services';
import { ThemeService } from './shared/services/theme.service';
import { AuthService, ScreenService } from 'grow-angular/services';
//import { log } from 'src/debug';
import { GrowTokenService } from './shared/services/grow-token-service';

import { licenseKey } from '../devextreme-license';
import config from 'devextreme/core/config';
//import { PrimeNGConfigType } from 'primeng/config';
import { PrimeNG } from 'primeng/config'; 

import { PrimaryColorService } from './pages-frontend/services/primary-color.service';

import { blendHexColor, hexToHsl } from './pages-frontend/utils/utils';

import { timer, startWith, switchMap } from 'rxjs';

import { LicenseManager } from 'primeng/api';
import { pNgLicenseKey, pNgPassKey } from '../primeng-license';

import { LibLocalizationService } from '@sitos-x/frontend-library';
import { TranslateService } from '@ngx-translate/core';
import type { Translation } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthMonitorService } from './shared/services/auth-monitor.service';
import { Router } from '@angular/router';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {

  private readonly primeng = inject(PrimeNG);
  //private readonly primengConfig = inject(PrimeNG);
  private readonly translate = inject(TranslateService);

  private readonly router = inject(Router);

  /**
   * PrimeNG-Voreinstellungen, einmal beim Start gesichert. `setTranslation()` ERSETZT das ganze
   * Objekt — ohne diese Basis verlören Paginator, Filter und Dialoge jedes Label, das der
   * `primeng`-Block nicht liefert.
   */
  private readonly primengDefaults: Translation = { ...this.primeng.translation };

  private readonly primaryColorService = inject(PrimaryColorService);
  readonly #destroyRef = inject(DestroyRef);
  private primaryColorIntervalDelay: number = 10000;

  readonly #libL10n = inject(LibLocalizationService);
  readonly #htmlLang = inject(HtmlLangService);
  readonly #pageTitle = inject(PageTitleStrategy);

  // KS, 20260827
  readonly #authMonitor = inject(AuthMonitorService);

  constructor(
    private auth: AuthService,
    private themeService: ThemeService,
    private screenService: ScreenService,
    private userService: UserService,
    private growTokenService: GrowTokenService,
    //private primengConfig: PrimeNG,
  ) {
    /**
     * Hält PrimeNGs `translation`-Store mit der ngx-translate-Sprache gleich — der Store kennt
     * ngx-translate nicht und ändert sich von selbst nie. Betrifft den Inline-Kalender des
     * `CalendarFilterDialog` (Monats-/Wochentagsnamen, Nav-`aria-label`, Wochenstart).
     *
     * `startWith` ist PFLICHT: `onLangChange` ist ein Subject ohne Replay, und die Sprache wird
     * gesetzt, bevor diese Komponente existiert — ohne das wartet das Abo ewig auf den nächsten
     * Wechsel. `get()` statt `instant()`, weil es auf einen laufenden Ladevorgang wartet.
     *
     * Im Konstruktor, weil `takeUntilDestroyed()` einen Injection-Context braucht.
     */
    this.translate.onLangChange
      .pipe(
        startWith(null),
        switchMap(() => this.translate.get('primeng')),
        takeUntilDestroyed(),
      )
      .subscribe((block) => {
        // `get()` gibt den KEY als String zurück, wenn der Block fehlt. Ein String an
        // `setTranslation()` würde die Konfiguration zerstören, deshalb der Typ-Guard.
        if (block !== null && typeof block === 'object') {
          // ngx-translate liefert ausschliesslich String-Blätter — `firstDayOfWeek` muss deshalb
          // als String in der JSON stehen und hier zurück in eine Zahl. Der Guard hält `NaN` aus
          // dem Store: das ergäbe einen LEEREN Wochentagskopf statt eines falschen ersten Tages.
          const firstDay = Number(block.firstDayOfWeek);

          this.primeng.setTranslation({
            ...this.primengDefaults,
            ...block as Translation,
            ...(Number.isFinite(firstDay) ? { firstDayOfWeek: firstDay } : {}),
          });
        }
      });

    // sitos-x-lib translation namespaces

    // um nur die base zu setzen und sonst die Defaults zu verwenden
    this.#libL10n.setRootKey('redesign');

    // explizit jeden namespace angeben, ohne einen default.
    this.#libL10n.setNamespaces({
      learningCard:                 'redesign.learningcard',
      mediaType:                    'redesign.mediaType',
      courseInfo:                   'redesign.dialogs.courseinfo',
      newsDetail:                   'redesign.dialogs.newsdetail',
      seeRatings:                   'redesign.dialogs.ratings',
      writeRating:                  'redesign.dialogs.writerating',
      newsWidget:                   'redesign.widgets.news',
      badgesWidget:                 'redesign.widgets.badges',
      calendarWidget:               'redesign.widgets.calendar',
      certificatesWidget:           'redesign.widgets.certificates',
      learningActivityWidget:       'redesign.widgets.learningActivity',
      myLearningContentWidget:      'redesign.widgets.myLearningContent',
      notifications:                'redesign.notifications',
      notificationsDetail:          'redesign.dialogs.notificationsdetail',
      myLatestCompletionsWidget:    'redesign.widgets.myLatestCompletions',
      lastCompletionsWidget:        'redesign.widgets.lastCompletions'
    });

  }

  ngOnInit() {

    // KS, 20260408
    //this.primengConfig.ripple.set(false);
    this.primeng.ripple.set(false);

    this.addResizeHandler();
    config({ licenseKey } as any);

    if(LicenseManager) {
      LicenseManager.verify(pNgLicenseKey, pNgPassKey);
    }

    this.configureTheme();

    // Basis-Titel sofort setzen (index.html liefert ein leeres <title>); den
    // Seitennamen ergänzt danach der Router-Hook der PageTitleStrategy.
    this.#pageTitle.init();

    // <html lang> auf die aktive UI-Sprache setzen und dort halten (Screenreader).
    this.#htmlLang.init();

    let token = this.auth.hasValidAccessToken();
    if (token) {
      this.loadUser();
    }

    this.addStorageHandler();

    timer(0, this.primaryColorIntervalDelay).pipe(
      switchMap(() => this.primaryColorService.primaryColorObservable$),
      takeUntilDestroyed(this.#destroyRef),
    ).subscribe((color: any) => {
      if (color) this.applyColorToDOM(color);
    });

    // KS, 20260827
    this.#authMonitor.startMonitoring();

    // KS, 20260914
    //this.addRouteChangeHandler();
  }

  // KS, 20260914
  /*
  private addRouteChangeHandler() {
    window.addEventListener('message', (e) => {

      console.info('addRouteChangeHandler !!!!!!!!!!!!');

      if (e.data.type === 'route_change') {
        const urlTree = this.router.parseUrl(e.data.route);
        const url = new URL(e.data.route, 'https://dummy/');
        const route = url.pathname;
        this.router.navigate([route], { queryParams: urlTree.queryParams });
      }

    });
  }
    */

  private configureTheme() {
    this.themeService.setAppTheme(localStorage.getItem('default_theme'));
  }

  private addResizeHandler() {
    // Aktiviert die ScreenService-Zustände (versorgt isMobile$/isTablet$/... via tap).
    // Teardown über das bestehende destroy$-Muster der Komponente.
    this.screenService._resize$.pipe(
      takeUntilDestroyed(this.#destroyRef),
    ).subscribe();
  }

  private addStorageHandler() {
    this.auth.addStorageStateHandler();
  }

  loadUser() {
    this.userService.getCurrentUser().subscribe();
  }

  private applyColorToDOM(color: string) {

    //this.primaryColorService.primaryColorObservable$.subscribe((color: any) => {
    
    if (color) {

      document.documentElement.style.setProperty('--primary-color', color);

      // ReDesign
      document.documentElement.style.setProperty('--variable-100', color);
      let colorCalc = blendHexColor(color, '#000000', 20);
      document.documentElement.style.setProperty('--variable-120', colorCalc);
      colorCalc = blendHexColor(color, '#000000', 40);
      document.documentElement.style.setProperty('--variable-140', colorCalc);
      colorCalc = blendHexColor(color, '#000000', 60);
      document.documentElement.style.setProperty('--variable-160', colorCalc);
      colorCalc = blendHexColor(color, '#ffffff', 20);
      document.documentElement.style.setProperty('--variable-80', colorCalc);
      colorCalc = blendHexColor(color, '#ffffff', 40);
      document.documentElement.style.setProperty('--variable-60', colorCalc);
      colorCalc = blendHexColor(color, '#ffffff', 60);
      document.documentElement.style.setProperty('--variable-40', colorCalc);
      colorCalc = blendHexColor(color, '#ffffff', 80);
      document.documentElement.style.setProperty('--variable-20', colorCalc);
      colorCalc = blendHexColor(color, '#ffffff', 90);
      document.documentElement.style.setProperty('--variable-10', colorCalc);
      colorCalc = blendHexColor(color, '#ffffff', 95);
      document.documentElement.style.setProperty('--variable-5', colorCalc);

      const [h, s, l] = hexToHsl(color);
      document.documentElement.style.setProperty('--variable-100-h', `${h}`);
      document.documentElement.style.setProperty('--variable-100-s', `${s}%`);
      document.documentElement.style.setProperty('--variable-100-l', `${l}%`);
      document.documentElement.style.setProperty('--variable-100-hsl', `${h}, ${s}%, ${l}%`);

    }

    //}
    
   }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent): void {
    if (event.data && event.data.type === 'auth:session_expired') {
      this.auth.initLogInFlow();
    }
  }

}
